import { FC, useEffect, useState } from "react";
import DiscardConfirmationModal from "../../common/discard-confirmation-modal/DiscardConfirmationModal";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { useAppDispatch } from "../../../hooks";
import { createBoxAction } from "../../../redux/actions/boxStorageAction";

const PERIOD_OPTIONS = ["3 Months", "6 Months", "1 Year", "Custom"] as const;
type PeriodOption = (typeof PERIOD_OPTIONS)[number];

const schema = Yup.object({
  boxStorageId: Yup.string().trim().required("Box Size is required"),
  timePeriod: Yup.string().optional(),
  customMonths: Yup.number().optional(),
  slotCount: Yup.number()
    .typeError("Please enter a valid number")
    .integer("Slot Count must be a whole number")
    .min(1, "Must be at least 1")
    .required("Slot Count is required"),
});

type BoxFormData = Yup.InferType<typeof schema>;

interface IAddEditBoxDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  populateFor?: {
    id?: number;
    boxStorageId: string;
    boxSize: string;
    timePeriod: string;
    slotCount: number;
  };

  onSave: (
    payload: {
      boxStorageId: string;
      boxSize: string;
      timePeriod: string;
      slotCount: number;
    },
    id?: number
  ) => Promise<void> | void;
  existingBoxes?: { id: number; boxStorageId: string }[];
}

export const AddEditBoxDrawer: FC<IAddEditBoxDrawerProps> = ({
  open,
  toggleDrawer,
  populateFor,
  onSave,
  existingBoxes = [],
}) => {
  const snackbar = useSnackbarContext();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setError,
  } = useForm<BoxFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      boxStorageId: "",
      timePeriod: "3 Months" as any,
      customMonths: undefined,
      slotCount: undefined,
    },
  });

  const normalizeId = (s?: string) => (s ?? "").trim().toLowerCase();
  const selectedTimePeriod = watch("timePeriod");

  const onInvalid = () => {
    const firstErr =
      errors.boxStorageId?.message ||
      errors.slotCount?.message ||
      errors.timePeriod?.message;
    if (firstErr) snackbar.showSnackbar(firstErr, "error");
  };

  useEffect(() => {
    if (!open) return;

    const existingPeriod = populateFor?.timePeriod ?? "";
    const isPresetPeriod = PERIOD_OPTIONS.includes(
      existingPeriod as PeriodOption
    );

    let timePeriod: PeriodOption | "" = "";
    let customMonths: number | undefined = undefined;

    if (isPresetPeriod) {
      timePeriod = existingPeriod as PeriodOption;
    } else if (existingPeriod) {
      const match = existingPeriod.match(/(\d+)/);
      if (match) {
        timePeriod = "Custom";
        customMonths = parseInt(match[1], 10);
      }
    }

    reset({
      boxStorageId: populateFor?.boxStorageId ?? "",
      timePeriod: timePeriod || ("" as unknown as PeriodOption),
      customMonths,
      slotCount: populateFor?.slotCount ?? undefined,
    });
  }, [open, populateFor, reset]);

  const handleSaveBox = async (data: BoxFormData) => {
    setLoading(true);
    try {
      const finalTimePeriod =
        data.timePeriod === "Custom"
          ? `${data.customMonths} Months`
          : data.timePeriod;

      const normalized = normalizeId(data.boxStorageId);

      const isDuplicate = (existingBoxes ?? []).some(
        (b) =>
          normalizeId(b.boxStorageId) === normalized && b.id !== populateFor?.id
      );

      if (isDuplicate) {
        setError("boxStorageId", {
          type: "manual",
          message: "Box Storage ID already exists",
        });
        snackbar.showSnackbar("Box Storage ID already exists", "error");
        setLoading(false);
        return;
      }

      if (populateFor?.id) {
        await onSave(
          {
            boxStorageId: data.boxStorageId.trim().toUpperCase(),
            boxSize: populateFor.boxSize,
            timePeriod: finalTimePeriod ?? "",
            slotCount: data.slotCount!,
          },
          populateFor.id
        );
      } else {
        await dispatch(
          createBoxAction({
            alias: data.boxStorageId.trim().toUpperCase(),
            quantity: data.slotCount!,
          })
        ).unwrap();
      }

      snackbar.showSnackbar(
        populateFor?.id
          ? "Box config updated successfully!"
          : "Box created successfully!",
        "success"
      );

      toggleDrawer();
    } catch (e: any) {
      if (e?.message?.includes("exists")) {
        setError("boxStorageId", {
          type: "manual",
          message: "Box Storage ID already exists",
        });
        snackbar.showSnackbar("Box Storage ID already exists", "error");
      } else {
        snackbar.showSnackbar(
          populateFor?.id
            ? "Failed to update box config. Please try again."
            : "Failed to create box. Please try again.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAttempt = () => {
    if (isDirty) {
      setExitConfirmOpen(true);
    } else {
      toggleDrawer();
    }
  };

  return (
    <>
    <DiscardConfirmationModal
      open={exitConfirmOpen}
      onKeepEditing={() => setExitConfirmOpen(false)}
      onDiscard={() => { reset(); setExitConfirmOpen(false); toggleDrawer(); }}
    />
    <CustomDrawer
      isOpen={open}
      toggleDrawer={handleCloseAttempt}
      sx={{ width: "28.65rem" }}
    >
      <form onSubmit={handleSubmit(handleSaveBox, onInvalid)}>
        <Box
          sx={{
            paddingInline: "1.857rem",
            paddingTop: "2rem",
            display: "flex",
            flexDirection: "column",
            gap: "2.5rem",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1.4rem",
              lineHeight: "1.7rem",
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            {populateFor ? "Edit Box Config" : "Add a New Box"}
          </Typography>

          <Box>
            <Typography>Box Size</Typography>
            <Controller
              name="boxStorageId"
              control={control}
              render={({ field }) => (
                <CustomTextBox
                  {...field}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  error={!!errors.boxStorageId}
                  helperText={errors.boxStorageId?.message}
                  sx={{
                    width: "100%",
                    backgroundColor: Colors.WHITE,
                    textTransform: "uppercase",
                  }}
                  placeholder="SMALL"
                />
              )}
            />
          </Box>

          <Box>
            <Typography>Slot Count</Typography>
            <Controller
              name="slotCount"
              control={control}
              render={({ field }) => (
                <CustomTextBox
                  {...field}
                  type="number"
                  inputProps={{ min: 1 }}
                  error={!!errors.slotCount}
                  helperText={errors.slotCount?.message}
                  sx={{
                    width: "100%",
                    backgroundColor: Colors.WHITE,
                    "& .MuiInputBase-root": {
                      height: "3rem",
                      fontSize: "1rem",
                    },
                    "& input": {
                      padding: "0 0.75rem",
                      height: "2.25rem",
                    },
                  }}
                  placeholder="e.g. 10"
                />
              )}
            />
          </Box>

          <CustomButton
            sx={{ width: "10rem", height: "2.8rem", mt: "1rem" }}
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : populateFor ? (
              "Update Box"
            ) : (
              "Add Box"
            )}
          </CustomButton>
        </Box>
      </form>
    </CustomDrawer>
    </>
  );
};
